import React, { useMemo, useState, useCallback } from "react";
import styles from "../styles/habitaciones.module.css";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import { showSuccessToast } from "../../../components/toast/toastBus";

import { useHabitaciones } from "../hooks/useHabitaciones";
import HabitacionesFilters from "../components/HabitacionesFilters";
import HabitacionesKpis from "../components/HabitacionesKpis";
import HabitacionesList from "../components/HabitacionesList";
import HabitacionFormModal from "../components/HabitacionesFormModal";

import { filterAndSortHabitaciones, computeHabitacionesKpis } from "../domain/habitacionesFilters";
import { initialHabitacionForm, mapHabitacionToForm } from "../domain/habitacionesForm";

export default function Habitaciones() {
  const { habitaciones, loading, error, createHabitacion, updateHabitacion, deleteHabitacion } = useHabitaciones();

  // filtros
  const [query, setQuery] = useState("");
  const [estado, setEstado] = useState("todos");

  // acordeones
  const [openSet, setOpenSet] = useState(new Set());
  const toggleOpen = useCallback((id) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const isOpen = useCallback((id) => openSet.has(id), [openSet]);

  // form modal
  const [formVisible, setFormVisible] = useState(false);
  const [editingHabitacion, setEditingHabitacion] = useState(null);
  const [formData, setFormData] = useState(initialHabitacionForm());

  const openCreate = () => {
    setEditingHabitacion(null);
    setFormData(initialHabitacionForm());
    setFormVisible(true);
  };

  const openEdit = (h) => {
    setEditingHabitacion(h);
    setFormData(mapHabitacionToForm(h));
    setFormVisible(true);
  };

  const closeForm = () => {
    setFormVisible(false);
    setEditingHabitacion(null);
    setFormData(initialHabitacionForm());
  };

  const onChangeForm = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();
    try {
      const esEdicion = !!editingHabitacion;
      if (esEdicion) {
        await updateHabitacion({ id: editingHabitacion.id, ...formData });
      } else {
        await createHabitacion(formData);
      }
      closeForm();
      showSuccessToast(esEdicion ? "Habitación actualizada con éxito" : "Habitación creada con éxito");
    } catch (err) {
      alert(err?.message || "Error al guardar habitación.");
    }
  };

  // delete modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const requestDelete = (h) => {
    if (h?.participacionEvento) {
      alert("No se puede eliminar una habitación asignada a un evento.");
      return;
    }
    setDeleteTarget(h);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteHabitacion(deleteTarget.id);
    } catch (err) {
      alert(err?.message || "Error al eliminar la habitación.");
    } finally {
      setShowConfirm(false);
      setDeleteTarget(null);
    }
  };

  const filtered = useMemo(
    () => filterAndSortHabitaciones({ habitaciones, query, estado }),
    [habitaciones, query, estado]
  );

  const kpi = useMemo(() => computeHabitacionesKpis(filtered), [filtered]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <div className={`${styles.card} ${styles.loading}`}>Cargando habitaciones…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap}>
          <div className={`${styles.card} ${styles.error}`}>
            <strong>Error:</strong> {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.wrap} ${styles.stack}`}>
        <div className={styles.titlebar}>
          <h2>Habitaciones</h2>
        </div>

        <HabitacionesFilters
          query={query}
          estado={estado}
          onChangeQuery={setQuery}
          onChangeEstado={setEstado}
          onClickNew={openCreate}
        />

        <HabitacionesKpis kpi={kpi} />

        <section className={styles.section}>
          <HabitacionesList
            list={filtered}
            isOpen={isOpen}
            onToggle={toggleOpen}
            onEdit={openEdit}
            onDelete={requestDelete}
          />
        </section>
      </div>

      <HabitacionFormModal
        open={formVisible}
        editing={!!editingHabitacion}
        formData={formData}
        onChange={onChangeForm}
        onClose={closeForm}
        onSubmit={onSubmitForm}
      />

      {showConfirm && (
        <ConfirmDeleteModal
          title="Confirmar Eliminación"
          message="¿Estás seguro de que deseas eliminar esta habitación? Esta acción no se puede deshacer."
          confirmWord="Eliminar"
          onClose={() => {
            setShowConfirm(false);
            setDeleteTarget(null);
          }}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
